import { Node, Edge, Connection, ReactFlowInstance, UpdateNodeInternals } from "reactflow";
import { AnchorProperties, handleAnchorDestinationLayoutChanged, handleAnchorNodeDrop } from "./AnchorUtil";
import {
  IDesignerSchema,
  getEmptySchemaId,
  getLoopVariableSchemaId,
  getSchema,
  getSchemaId,
  schemaExists
} from "../NodeTypes/DataSchemas";
import {
  DocumentSchemaType,
  getIdentifierName,
  getIncludedFieldName,
  IDocumentListSchema,
  IDocumentObjectSchema,
  isReferenceSchema
} from "../FlowDocument/FlowDocumentModel";
import {
  IDataParam,
  IDataPropertyCountOptions,
  IDataTypeOptions,
  IFlowInputEditorOptions,
  INodeProperty,
  INodeTypeDefinition,
  NodeType,
  ReferencePropertyType
} from "../NodeTypes/Base";
import { BannerStatus } from "@plex/react-components";
import { NodeConfigPropertyType } from "../FlowDocument/PropertyTypeDefinitions";
import {
  buildNodeInputsFromSchema,
  checkAndRemoveUnmappable,
  createDataParamsFromSchema,
  deleteEdgesFromNodes,
  getInputHandle,
  getOutputHandle,
  handleEdgeConnections,
  updateLinkedSchemas
} from "./EdgeUtil";
import { AddMenuNodePopulater, IDataTypeConfig } from "../AddNodeMenu/AddMenuNodePopulation";
import {
  DataType,
  convertToList,
  shouldConvertToList,
  dataTypes,
  isObjectLike,
  isListType
} from "../NodeTypes/TypeDefinitions";
import { addDataPropertiesFromCount, getDataPropertyCountConfigForProperty } from "../NodePropertiesForm/DataPropertyCountFormField";
import { camelCaseToSpacesRegEx } from "../NodePropertiesForm/FormConstants";
import { upperSnakeToCamelCase } from "../FlowDocument/DocumentProcessor";
import { IViewController } from "../ViewContext";
import { getFlowInputName } from "../NodeTypes/SchemaSystem";
import { createNewNodeMeta } from "../DesignerContainer/DesignerContainer";
import { openConfirmDialog } from "./DialogUtil";
import { variableInPropertyName, variableOutPropertyName } from "../UserDefinedSchemaManagement/LoopVariableEditor";

const lastTimeoutId: { [nodeId: string]: any } = {};
const lastSecondaryTimeoutId: { [nodeId: string]: any } = {};

export const updateNodeTimestamp = (node: Node<any> | undefined) => {
  if (node?.data) {
    node.data.nodeUpdateTimestamp = (node.data.nodeUpdateTimestamp ?? 0) + 1;
  }
};

export const forceNodeUpdate = (
  node: Node<any> | undefined,
  updateNodeInternals: UpdateNodeInternals,
  reactFlowInstance: ReactFlowInstance,
  callback: (() => void) | undefined = undefined,
  timeout: number = 80,
  nodes?: Node<any>[]
) => {
  updateNodeTimestamp(node);
  let currentNodes: Node<any>[];
  if (nodes) {
    currentNodes = nodes;
  } else {
    currentNodes = reactFlowInstance.getNodes();
  }
  let updateNodes: Node[] = [];

  if (!node) {
    return;
  }

  node.position.x += 0.0001;
  updateNodes.forEach((n) => {
    n.position.x += 0.0001;
    updateNodeTimestamp(n);
  });
  updateNodeInternals(node.id);
  if (lastTimeoutId[node.id] !== null) {
    clearTimeout(lastTimeoutId[node.id]);
  }
  lastTimeoutId[node.id] = setTimeout(() => {
    node.position.x -= 0.0001;
    updateNodes.forEach((n) => {
      n.position.x -= 0.0001;
      updateNodeInternals(n.id);
      updateNodeTimestamp(n);
    });
    updateNodeInternals(node.id);
    lastTimeoutId[node.id] = null;

    if (node.data.nodeProperties.anchoredToNodeId || node.data.nodeProperties.anchoredNodes?.length > 0) {
      updateNodes = handleAnchorDestinationLayoutChanged(node.id, reactFlowInstance, updateNodeInternals, nodes);
      updateNodes.forEach((n) => {
        n.position.x += 0.0001;
        updateNodeInternals(n.id);
        updateNodeTimestamp(n);
      });

      if (lastSecondaryTimeoutId[node.data.nodeProperties.anchoredToNodeId] !== null) {
        clearTimeout(lastTimeoutId[node.data.nodeProperties.anchoredToNodeId]);
      }
      lastSecondaryTimeoutId[node.data.nodeProperties.anchoredToNodeId] = setTimeout(() => {
        updateNodes.forEach((n) => {
          n.position.x -= 0.0001;
          updateNodeInternals(n.id);
          updateNodeTimestamp(n);
        });
        lastSecondaryTimeoutId[node.data.nodeProperties.anchoredToNodeId] = null;
      }, timeout);

      if (node.data.nodeProperties.anchoredToNodeId) {
        forceNodeUpdate(
          currentNodes.find((n: Node<any>) => n.id === node.data.nodeProperties.anchoredToNodeId)!,
          updateNodeInternals,
          reactFlowInstance,
          undefined,
          undefined,
          nodes
        );
      }
    }

    if (callback) {
      callback();
    }
  }, timeout);
};

export const getNodeOutputs = (node: Node<any>) => {
  if (Object.keys(node!.data.nodeProperties.inputs).length > 0) {
    const nodeInput: IDataParam = node!.data.nodeProperties.inputs[Object.keys(node!.data.nodeProperties.inputs)[0]!];
    let inputSchema: IDesignerSchema = getSchema(nodeInput.schemaId!)!;
    if (inputSchema && inputSchema.schemaType === DocumentSchemaType.list) {
      inputSchema = getSchema((inputSchema as unknown as IDocumentListSchema).listItemSchema)!;
    }
    if (inputSchema && inputSchema.schemaType === DocumentSchemaType.object) {
      const schemaProperties = (inputSchema as unknown as IDocumentObjectSchema).properties;
      return Object.keys(schemaProperties).map((propertyName: string) => {
        const propertySchemaId: string = schemaProperties[propertyName]?.schema!;
        const camelCaseSchema = upperSnakeToCamelCase(propertySchemaId);
        const primitiveSchema = dataTypes.find((dt: string) => dt === camelCaseSchema);
        return {
          name: propertyName,
          type: primitiveSchema ?? propertySchemaId,
          schemaId: propertySchemaId,
          label: schemaProperties[propertyName]?.label,
          enabled: node.type === "callDataSource"
        } as IDataParam;
      });
    }
  }
  return [];
};

const getDefaultValue = (dataType: DataType) => {
  switch (dataType) {
    case DataType.BOOLEAN:
      return false;
    case DataType.INTEGER:
      return 0;
    case DataType.DECIMAL:
      return 0;
    default:
      return "";
  }
};

export const resetNodeDataValuesToDefault = (node: Node<any>) => {
  if (node) {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!)!;
    const dataTypeProperty = nodeDefinition.nodeConfigProperties.find(
      (p: INodeProperty) => p.propertyType === NodeConfigPropertyType.DataType
    );
    if (dataTypeProperty) {
      const valueProperties = nodeDefinition.nodeConfigProperties.filter(
        (p: INodeProperty) => p.propertyType === NodeConfigPropertyType.DataValue
      );
      valueProperties.forEach((valueProperty: INodeProperty) => {
        const dataType = node.data.nodeProperties[dataTypeProperty.name];
        node.data.nodeProperties[valueProperty.name] = getDefaultValue(dataType);
      });
    }
  }
};

const removeNodeFlowInputs = (nodeId: string, nodeType: string, viewController: IViewController) => {
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(nodeType);
  const flowInputProperties = nodeDefinition?.nodeConfigProperties.filter(
    (prop: INodeProperty) => prop.propertyType === NodeConfigPropertyType.FlowInputDataEditor
  );
  flowInputProperties?.forEach((prop: INodeProperty) => {
    const inputEditorOptions = prop.options as unknown as IFlowInputEditorOptions;
    const inputName = getFlowInputName(nodeId, inputEditorOptions.inputSubName);
    viewController.flowInputController.deleteProperty(inputName);
  });
};

export const deleteSelectedNodes = (
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals,
  viewController: IViewController
) => {
  let selectedNodes = reactFlowInstance.getNodes().filter((n) => n.selected);
  if (selectedNodes.some((n: Node<any>) => globalThis.nodeTypeDefinitions.getDefinition(n.type!)!.confirmDeletion)) {
    openConfirmDialog("Are you sure you want to delete this node?", () =>
      deleteNodes(selectedNodes, updateNodeInternals, reactFlowInstance, viewController)
    );
  } else {
    deleteNodes(selectedNodes, updateNodeInternals, reactFlowInstance, viewController);
  }
};

export const deleteNodes = (
  deletingNodes: Node[],
  updateNodeInternals: UpdateNodeInternals,
  reactFlowInstance: ReactFlowInstance,
  viewController: any
) => {
  if (deletingNodes.some((n) => n.data.nodeProperties.anchoredNodes?.length > 0)) {
    viewController.bannerContext.addMessage("Cannot delete, node must not be anchored.", BannerStatus.warning);
    return;
  }

  const deletingEdges = reactFlowInstance
    .getEdges()
    .filter((e: Edge) => deletingNodes.some((n: Node<any>) => n.id === e.source || n.id === e.target));

  checkAndRemoveUnmappable(
    deletingEdges.map((edge: Edge) => {
      return { edge: edge, newSchema: undefined };
    }),
    () => {
      // Need to first remove the node from anchored nodes list on the parent
      reactFlowInstance.setNodes((nds) => {
        nds.forEach((updatedNode: Node<any>) => {
          const anchoredDeletingNode = deletingNodes.find(
            (deletingNode: Node<any>) => deletingNode.data.nodeProperties.anchoredToNodeId === updatedNode.id
          );
          if (anchoredDeletingNode) {
            delete anchoredDeletingNode.data.nodeProperties.anchoredToNodeId;
          }
          updatedNode.data.nodeProperties.anchoredNodes = updatedNode.data.nodeProperties.anchoredNodes.filter(
            (anchor: AnchorProperties) =>
              !deletingNodes.find((deletingNode: Node<any>) => anchor.nodeId === deletingNode.id)
          );
        });
        return nds;
      });

      setTimeout(() => {
        const newEdges = deleteEdgesFromNodes(
          deletingNodes.map((n: Node<any>) => n.id),
          reactFlowInstance,
          updateNodeInternals
        );
        updateLinkedSchemas(reactFlowInstance.getNodes(), newEdges, updateNodeInternals, reactFlowInstance);
        reactFlowInstance.setNodes((nds) => {
          nds.forEach((n: Node) => {
            n.data.nodeProperties.anchoredNodes = n.data.nodeProperties.anchoredNodes.filter(
              (anchor: AnchorProperties) =>
                !deletingNodes.find((deletingNode: Node<any>) => anchor.nodeId === deletingNode.id)
            );
          });
          return nds.filter((n) => !deletingNodes.some((dn) => n.id === dn.id));
        });
      }, 30);
    },
    reactFlowInstance,
    updateNodeInternals
  );

  deletingNodes.forEach((node: Node<any>) => removeNodeFlowInputs(node.id, node.type!, viewController));
};

export const isTriggerNode = (nodeType: string) => {
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(nodeType);
  return nodeDefinition?.controlOutputOnly ?? false;
};

export const canAddNode = (nodeType: string, reactFlowInstance: ReactFlowInstance) => {
  if (isTriggerNode(nodeType) && reactFlowInstance.getNodes().some((n) => isTriggerNode(n.data.nodeProperties.type))) {
    return false;
  }

  return true;
};

export interface IAnchor {
  parentNode: Node<any>;
  node: Node<any>;
}

export interface IAutoCreateResult {
  nodes: Node<any>[];
  connections: Connection[];
  anchors: IAnchor[];
}

const getAutoCreateNodeResult = (
  node: Node<any>,
  dataParams: IDataParam[],
  direction: "input" | "output",
  existingConnections: (Edge<any> | Connection)[] = [],
  connectToDataParamFilter?: string[]
) => {
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
  if (
    (direction === "output" && nodeDefinition?.inputsSchemaProperties) ||
    (direction === "input" && nodeDefinition?.outputsSchemaProperties)
  ) {
    return {
      nodes: [],
      connections: [],
      anchors: []
    };
  }

  const nodes: Node<any>[] = [];
  const connections: Connection[] = [];
  const anchors: IAnchor[] = [];
  const enabledObjectDataParamNames = Object.keys(dataParams).filter(
    (dataParamName: string) =>
      dataParams[dataParamName].type === DataType.OBJECT &&
      dataParams[dataParamName].enabled &&
      (connectToDataParamFilter === undefined || connectToDataParamFilter.includes(dataParamName))
  );
  enabledObjectDataParamNames.forEach((dataParamName: string) => {
    const existingConnection = existingConnections.find(
      (c) =>
        (direction === "output" &&
          c.target === node.id &&
          c.targetHandle === getOutputHandle(dataParamName, DataType.OBJECT).toHandleId()) ||
        (direction === "input" &&
          c.source === node.id &&
          c.sourceHandle === getInputHandle(dataParamName, DataType.OBJECT).toHandleId())
    );
    if (existingConnection || !dataParams[dataParamName].enabled) {
      return;
    }

    const autoCreatedNode = createNewNodeMeta(
      direction === "output" ? NodeType.objectProperties : NodeType.buildObjectFromProperties,
      null,
      null,
      null,
      null,
      node.position.x,
      node.position.y,
      undefined,
      {},
      null
    );
    autoCreatedNode.position.x = node.position.x;
    autoCreatedNode.position.y = node.position.y;
    nodes.push(autoCreatedNode);

    const connection: Connection = {
      target: direction === "output" ? node.id : autoCreatedNode.id,
      targetHandle:
        direction === "output"
          ? getOutputHandle(dataParamName, DataType.OBJECT).toHandleId()
          : getOutputHandle("object", DataType.OBJECT).toHandleId(),
      source: direction === "output" ? autoCreatedNode.id : node.id,
      sourceHandle:
        direction === "output"
          ? getInputHandle("object", DataType.OBJECT).toHandleId()
          : getInputHandle(dataParamName, DataType.OBJECT).toHandleId()
    };
    connections.push(connection);
    anchors.push({ parentNode: node, node: autoCreatedNode });
  });
  return {
    nodes: nodes,
    connections: connections,
    anchors: anchors
  };
};

export const autoCreateNodes = (
  node: Node<any>,
  existingConnections: (Edge<any> | Connection)[] = []
): IAutoCreateResult => {
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
  if (nodeDefinition?.controlInputOnly && nodeDefinition.innerLoop) {
    return getAutoCreateNodeResult(node, node.data.nodeProperties.inputs, "input", existingConnections, [
      variableInPropertyName
    ]);
  }

  return getAutoCreateNodeResult(node, node.data.nodeProperties.outputs, "output", existingConnections);
};

export const autoCreateNodesForLoop = (loopNode: Node<any>, existingConnections: (Edge<any> | Connection)[] = []) => {
  return getAutoCreateNodeResult(loopNode, loopNode.data.nodeProperties.outputs, "output", existingConnections, [
    variableOutPropertyName
  ]);
};

export const applyAutoCreateResult = (
  autoCreateResult: IAutoCreateResult,
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals,
  callback?: () => void
) => {
  reactFlowInstance.addNodes(autoCreateResult.nodes);
  if (autoCreateResult.connections.length > 0) {
    // Need to wait for reactflow to add the node after calling addNodes(),
    //   because it cannot add nodes synchronously
    setTimeout(() => {
      var connectionResult = handleEdgeConnections(autoCreateResult.connections);
      autoCreateResult.anchors.forEach((anchor: IAnchor) => {
        const newEdgesState = handleAnchorNodeDrop(
          anchor.node,
          connectionResult.newEdges,
          AddMenuNodePopulater._reactFlowInstance,
          updateNodeInternals,
          false,
          anchor.parentNode
        );
        AddMenuNodePopulater._reactFlowInstance.setEdges(newEdgesState);
        handleAnchorDestinationLayoutChanged(
          anchor.parentNode.id,
          AddMenuNodePopulater._reactFlowInstance,
          updateNodeInternals
        );
      });

      if (callback) {
        callback();
      }
    }, 80);
  } else {
    if (callback) {
      callback();
    }
  }
};

export const updateNodeDataProperties = (node: Node<any>) => {
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!)!;
  const dataTypeConfigs = nodeDefinition.nodeConfigProperties.filter(
    (p: INodeProperty) => p.propertyType === NodeConfigPropertyType.DataType
  );
  dataTypeConfigs.forEach((dataTypeConfig: INodeProperty) => {
    if (!node!.data.nodeProperties[dataTypeConfig.name]) {
      return;
    }

    const selectingFromListTypes = ((dataTypeConfig.options as IDataTypeOptions)?.dataTypes ?? dataTypes).some(
      (t: string) => isListType(t as DataType)
    );

    const dataTypeOptions = dataTypeConfig.options as IDataTypeOptions;
    node!.data.nodeProperties.isNowValue = false;
    let referenceSchema: IDocumentObjectSchema | undefined = undefined;
    const referenceInputName = dataTypeOptions.dataProperties.find(
      (propertyName: string) => node!.data.nodeProperties.inputs[propertyName]?.type === DataType.OBJECTREFERENCE
    );
    const referenceOutputName = dataTypeOptions.dataProperties.find(
      (propertyName: string) => node!.data.nodeProperties.outputs[propertyName]?.type === DataType.OBJECTREFERENCE
    );
    let referenceSchemaId: string | undefined = undefined;
    if (referenceInputName) {
      referenceSchemaId = node!.data.nodeProperties.inputs[referenceInputName].schemaId;
      if (referenceSchemaId !== undefined) {
        const schema = getSchema(referenceSchemaId) as unknown as IDocumentObjectSchema;
        if (schema.schemaType === DocumentSchemaType.object && isReferenceSchema(schema)) {
          referenceSchema = schema;
        }
      }
    }
    if (referenceOutputName) {
      referenceSchemaId = node!.data.nodeProperties.outputs[referenceOutputName].schemaId;
      if (referenceSchemaId !== undefined) {
        const schema = getSchema(referenceSchemaId) as unknown as IDocumentObjectSchema;
        if (schema.schemaType === DocumentSchemaType.object && isReferenceSchema(schema)) {
          referenceSchema = schema;
        }
      }
    }
    let referenceLabel: string | undefined = undefined;
    if (dataTypeOptions.referenceProperty === ReferencePropertyType.identifier) {
      if (referenceSchema?.properties) {
        const identifierName = getIdentifierName(referenceSchema);
        if (identifierName) {
          referenceLabel = identifierName.replaceAll("_", " ");
          referenceLabel = camelCaseToReadable(referenceLabel);
        }
      }
    }
    if (dataTypeOptions.referenceProperty === ReferencePropertyType.includedField) {
      if (referenceSchema) {
        const includedFieldName = getIncludedFieldName(referenceSchema);
        if (includedFieldName) {
          referenceLabel = includedFieldName.replaceAll("_", " ");
          referenceLabel = camelCaseToReadable(referenceLabel);
        }
      }
    }
    dataTypeOptions.dataProperties.forEach((dataPropertyName: string) => {
      const inputDataProperty: IDataParam = node!.data.nodeProperties.inputs[dataPropertyName];
      const outputDataProperty: IDataParam = node!.data.nodeProperties.outputs[dataPropertyName];
      const configDataType: string = node!.data.nodeProperties[dataTypeConfig.name];
      if (inputDataProperty && inputDataProperty.type !== DataType.OBJECTREFERENCE) {
        const isObject = isObjectLike(configDataType as DataType);
        let dataType = configDataType as DataType;
        if (shouldConvertToList(dataType, inputDataProperty, selectingFromListTypes)) {
          dataType = convertToList(dataType);
        }
        node!.data.nodeProperties.inputs[dataPropertyName] = {
          ...inputDataProperty,
          type: dataType,
          schemaId: isObject ? getEmptySchemaId(dataType) : getSchemaId(dataType),
          label: referenceLabel ?? undefined
        };
      }
      if (outputDataProperty && outputDataProperty.type !== DataType.OBJECTREFERENCE) {
        const isObject = isObjectLike(configDataType as DataType);
        let dataType = configDataType as DataType;
        if (shouldConvertToList(dataType, outputDataProperty, selectingFromListTypes)) {
          dataType = convertToList(dataType);
        }
        node!.data.nodeProperties.outputs[dataPropertyName] = {
          ...outputDataProperty,
          type: dataType,
          schemaId: isObject ? getEmptySchemaId(dataType) : getSchemaId(dataType),
          label: referenceLabel ?? undefined
        };
      }

      const propertyCountConfig = getDataPropertyCountConfigForProperty(nodeDefinition, dataPropertyName);
      if (propertyCountConfig) {
        addDataPropertiesFromCount(
          node,
          propertyCountConfig.name,
          propertyCountConfig.options as IDataPropertyCountOptions
        );
      }
    });
  });
};

export const refreshNodeDataPropertiesFromSchema = (
  schemaId: string,
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals
) => {
  reactFlowInstance.getNodes().forEach((node: Node<any>) => {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
    if (nodeDefinition?.inputsSchemaProperties) {
      const schemaIdForProperties = node.data.nodeProperties[nodeDefinition?.inputsSchemaProperties.schemaPropertyName];
      if (schemaIdForProperties == schemaId) {
        const previousInputs = node.data.nodeProperties.inputs ?? {};
        node.data.nodeProperties.inputs = buildNodeInputsFromSchema(
          node,
          schemaIdForProperties,
          nodeDefinition,
          reactFlowInstance
        );
        // Preserve enabled state from before the rebuild so that user-selected
        // properties are not cleared when the underlying schema changes
        // (e.g. when a property is added to a User Defined Object).
        Object.keys(node.data.nodeProperties.inputs).forEach((inputName: string) => {
          if (previousInputs[inputName]?.enabled) {
            node.data.nodeProperties.inputs[inputName].enabled = true;
          }
        });
        forceNodeUpdate(node, updateNodeInternals, reactFlowInstance);
      }
    }
    if (nodeDefinition?.outputsSchemaProperties) {
      const objectInputName = Object.keys(node.data.nodeProperties.inputs).find(
        (inputName: string) => node.data.nodeProperties.inputs[inputName].type === DataType.OBJECT
      );
      if (objectInputName) {
        const schemaIdForProperties = node.data.nodeProperties.inputs[objectInputName].schemaId;
        if (schemaIdForProperties == schemaId) {
          const previousOutputs = node.data.nodeProperties.outputs ?? {};
          node.data.nodeProperties.outputs = createDataParamsFromSchema(
            node,
            schemaIdForProperties,
            false,
            reactFlowInstance
          );
          Object.keys(node.data.nodeProperties.outputs).forEach((outputName: string) => {
            if (previousOutputs[outputName]?.enabled) {
              node.data.nodeProperties.outputs[outputName].enabled = true;
            }
          });
          forceNodeUpdate(node, updateNodeInternals, reactFlowInstance);
        }
      }
    }
  });
};

export const setSchemaIdForLoopEndVariables = (
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals,
  currentEdges?: Edge[]
) => {
  reactFlowInstance.getNodes().forEach((node: Node<any>) => {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!)!;
    if (nodeDefinition.controlInputOnly && nodeDefinition.innerLoop) {
      const loopNodeId = node.data.nodeProperties.insideLoopNodeId;
      const loopVariableSchemaId = getLoopVariableSchemaId(loopNodeId);
      const variablesInput = node.data.nodeProperties.inputs[variableInPropertyName];
      if (variablesInput.schemaId !== loopVariableSchemaId) {
        const variablesInputHasConnections = (currentEdges ?? reactFlowInstance.getEdges()).some(
          (e: Edge) =>
            e.source === node.id &&
            e.sourceHandle === getInputHandle(variableInPropertyName, DataType.OBJECT).toHandleId()
        );

        if (schemaExists(loopVariableSchemaId)) {
          variablesInput.schemaId = loopVariableSchemaId;
          const loopVariableSchema = getSchema(loopVariableSchemaId);
          if (loopVariableSchema?.schemaType === DocumentSchemaType.object) {
            variablesInput.enabled =
              variablesInputHasConnections ||
              Object.keys((loopVariableSchema as unknown as IDocumentObjectSchema).properties).length > 0;
          }
        } else {
          variablesInput.schemaId = getEmptySchemaId(DataType.OBJECT);
          variablesInput.enabled = variablesInputHasConnections;
        }
        const autoCreateResult = autoCreateNodes(node, reactFlowInstance.getEdges());
        applyAutoCreateResult(autoCreateResult, reactFlowInstance, updateNodeInternals);
        forceNodeUpdate(node, updateNodeInternals, reactFlowInstance);
      }
    }
  });
};

export const getAvailableConfigDataTypes = (nodeType: string, dataPropertyName: string) => {
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(nodeType)!;
  let availableDataTypes: IDataTypeConfig[] = [];

  // Get properties linked to this one, since property count clones data properties we may need to look at the clone source.
  const linkedPropertyNames: string[] = [dataPropertyName];
  const linkedConfigs = nodeDefinition.nodeConfigProperties
    .filter((config: INodeProperty) => config.propertyType === NodeConfigPropertyType.DataPropertyCount)
    .filter(
      (config: INodeProperty) =>
        dataPropertyName.indexOf((config.options as IDataPropertyCountOptions)?.cloneFrom) === 0
    );
  linkedConfigs.forEach((config: INodeProperty) =>
    linkedPropertyNames.push((config.options as IDataPropertyCountOptions)?.cloneFrom)
  );
  availableDataTypes = availableDataTypes.concat(
    nodeDefinition.dataOutputs
      .filter((dataOutput: IDataParam) =>
        linkedConfigs.some((nodeProperty: INodeProperty) => nodeProperty.name === dataOutput.name)
      )
      .map((dataOutput: IDataParam) => {
        return { dataType: dataOutput.type as string } as IDataTypeConfig;
      })
  );
  const dynamicTypeProperties = nodeDefinition.nodeConfigProperties
    .filter((p: INodeProperty) => p.propertyType === NodeConfigPropertyType.DataType)
    .map((property: INodeProperty) => property) as INodeProperty[];
  const dynamicTypeProperty = dynamicTypeProperties.filter((dataTypeProperty: INodeProperty) =>
    linkedPropertyNames.some(
      (linkedName: string) =>
        (dataTypeProperty?.options as IDataTypeOptions)?.dataProperties.some(
          (dataPropertyName: string) => dataPropertyName === linkedName
        )
    )
  )[0];
  const dynamicTypeOptions = dynamicTypeProperty?.options as IDataTypeOptions;
  if (dynamicTypeProperty) {
    const propertyTypes: IDataTypeConfig[] = (dynamicTypeOptions.dataTypes ?? dataTypes).map((d: DataType) => {
      return {
        configProperties: [{ propertyName: dynamicTypeProperty.name, propertyValue: d }],
        dataType: d
      };
    });
    availableDataTypes = availableDataTypes
      .filter(
        (config: IDataTypeConfig) =>
          !propertyTypes.some((propertyTypeConfig: IDataTypeConfig) => propertyTypeConfig.dataType === config.dataType)
      )
      .concat(propertyTypes);
  }

  // If no types were added from dynamic types, add types directly from inputs/outputs
  if (availableDataTypes.length === 0) {
    availableDataTypes = availableDataTypes.concat(
      nodeDefinition.dataInputs
        .filter((dataInput: IDataParam) => dataInput.name === dataPropertyName)
        .map((dataInput: IDataParam) => {
          return { dataType: dataInput.type as string } as IDataTypeConfig;
        })
    );
    availableDataTypes = availableDataTypes.concat(
      nodeDefinition.dataOutputs
        .filter((dataOutput: IDataParam) => dataOutput.name === dataPropertyName)
        .map((dataOutput: IDataParam) => {
          return { dataType: dataOutput.type as string } as IDataTypeConfig;
        })
    );
  }

  return availableDataTypes;
};

export const camelCaseToReadable = (camelCase: string) => {
  const readableName = camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
  return readableName.replace(camelCaseToSpacesRegEx, "$1$4 $2$3$5");
};

export const setNodeSelection = (selectedNodeIds: string[], reactFlowInstance: ReactFlowInstance) => {
  const nodes = reactFlowInstance.getNodes();
  const currentSelectedNodes = nodes.filter((n) => n.selected);
  currentSelectedNodes.forEach((n) => (n.selected = false));
  nodes.filter((n) => selectedNodeIds.includes(n.id)).forEach((n) => (n.selected = true));
  reactFlowInstance.setNodes(nodes);
};

export const isFeatureToggleOn = (featureToggleKey: string): boolean => {
  // @ts-ignore
  return plex?.env?.features?.[featureToggleKey] === true;
};

export const isSettingEnabled = (settingKey: string): boolean => {
  const settingValue = globalThis.flowDesignerSettings?.[settingKey];
  return typeof settingValue === "string" && settingValue.trim().toLowerCase() === "true";
};

export const isMenuVisible = (nodeDefinition: INodeTypeDefinition, experimentalModeState: boolean): boolean => {
  if (nodeDefinition.featureToggle !== undefined) {
    return isFeatureToggleOn(nodeDefinition.featureToggle);
  }
  if (nodeDefinition.setting !== undefined) {
    return isSettingEnabled(nodeDefinition.setting);
  }
  return (!experimentalModeState && !nodeDefinition.isExperimental) || experimentalModeState;
};
